/**
 * Unified Event System
 * 
 * Data-driven event system where all events are tuples [eventName, ...args]
 * This simplifies the mental model and makes data flow clearer.
 */

import { Position } from '../voxelMath'
import type { SnapshotMeta } from '@/document/model'
import { VoxelMap, VoxelData, Face } from '../voxelTypes'
import type { PaintSettings, ScribbleSettings } from '@/uiState'
import type { Spine } from '@/interactions/spineCore'
import type { BrushStrokePayload } from '@/brush/rasterize'

// ============================================================================
// Event Tuple Format
// ============================================================================


// Specific event types for type safety
export type VoxelEvent = 
  | readonly ["voxel.blend", VoxelMap | Array<number>, 'place' | 'fill' | 'subtract' | 'mask' | 'xor' | 'paint']  // Blend voxels with specified mode
  | readonly ["voxel.extrude", { anchor: Position, face: Face, offset: number, fillMode?: boolean }]
  | readonly ['voxel.extrudeFaces', { face: Face, keys: ReadonlyArray<number>, offset: number, fillMode?: boolean }]

// Note: SelectionOp is defined below in unified selection types

export type SelectionEvent =
  | readonly ["selection.change.clear"]  // Clear current selection
  | readonly ["selection.change.selectAll"]
  | readonly ["selection.change.floodFillFromPosition", Position, { mode?: 'connected' | 'material' | 'face', face?: 'x+'|'x-'|'y+'|'y-'|'z+'|'z-', op?: SelectionOp }?]
  | readonly ["selection.change.selectInBounds", { min: Position, max: Position }, { op?: SelectionOp }?]  // Select all voxels in bounds
  | readonly ["selection.change.selectPositions", ReadonlyArray<Position>, { op?: SelectionOp }?]
  | readonly ["doc.selection.commit", Position?]  // Commit transform to base and rebase selection (optional delta payload)
  | readonly ["doc.selection.delete"]  // Delete selected voxels

export type PaintStroke = readonly ["paint.stroke", { 
      spine: Map<number, { normal?: Position }>,
      scribble: ScribbleSettings,
      paint: PaintSettings,
      voxel: VoxelData,
      mode: 'color' | 'type',
      seedNormal?: Position,
    }]
export type PaintRect = readonly ["paint.rectangle", { min: Position, max: Position, normal?: Position, mode: 'place'|'fill'|'subtract', voxel: VoxelData }]
export type PaintBucket = readonly ["paint.bucket", { anchor: Position, paint: PaintSettings, voxel: VoxelData, seedNormal?: Position }]

// Scribble/pen/eraser stroke via spine expansion
export type ScribbleEvent =
  | readonly ["scribble.apply" | "scribble.append", { 
      spine: Spine,
      scribble: ScribbleSettings,
      subtract: boolean,
      /** Scoped dig (B5): a subtract stroke removes only cells of this canonical type. */
      onlyType?: string,
    }]

export type ToolType =
  | 'scribble'
  | 'paintbrush'
  | 'paintbucket'
  | 'eyedropper'
  | 'eraser'
  | 'select'
  | 'shape'
  | 'line'
  | 'spline'

export type ToolEvent =
  | readonly ["ui.setTool", ToolType]
  | readonly ["ui.tool.fillMode", boolean]
  | readonly ["ui.tool.paletteIndex", number]
  | readonly ["ui.tool.selectedShape", 'sphere' | 'cylinder' | 'cone' | null]

export type PaletteEvent =
  | readonly ['palette.setItems', ReadonlyArray<VoxelData>]
  | readonly ['palette.setItem', number, VoxelData]
  | readonly ['palette.setItemColor', number, number]

export type GeometryEvent =
  | readonly ["geom.shape", {
      type: 'sphere' | 'cylinder' | 'cone',
      center: Position,
      radius: number,
      height?: number,
      axis?: 'x' | 'y' | 'z',
      axisSign?: 1 | -1,
      voxel: VoxelData,
      rotation?: { x:number;y:number;z:number;w:number }
    }]
  | readonly ["geom.translate", string, {
      dx: number,
      dy: number, 
      dz: number
    }]

export type EphemeralEvent =
  | readonly ["ui.update", (prev: any) => any]
  | readonly ["ui.set", Record<string, any>]
  | readonly ["ui.setIn", string[], any]
  

export type SystemEvent =
  | readonly ["undo"]
  | readonly ["redo"]
  | readonly ["transaction", ReadonlyArray<EventTuple>, string?]

export type MetaEvent =
  | readonly ["meta.set", Partial<SnapshotMeta>]
  | readonly ["meta.setIn", string[], any]

// Swept brush stroke (see src/events/brushStroke.ts): brush.stroke is an undo boundary, .append is draft-only
export type BrushStrokeEvent =
  | readonly ["brush.stroke" | "brush.stroke.append", BrushStrokePayload]

/** Durable single-purpose world events (src/experiences), always `experience.<world>.<verb>`. */
export type ExperienceEvent = readonly [`experience.${string}`, unknown]

export type EventTuple = VoxelEvent | SelectionEvent | PaintStroke | PaintRect | PaintBucket | ScribbleEvent | BrushStrokeEvent | ToolEvent | PaletteEvent | GeometryEvent | EphemeralEvent | SystemEvent | SelectionChangeEvent | FaceSelectionUnifiedEvent | MetaEvent | ExperienceEvent

// ============================================================================
// High-level Selection Apply (unifying API)
// ============================================================================

export type SelectionOp = 'replace' | 'add' | 'subtract'
export type SelectionSource =
  | { kind: 'flood', anchor: Position, sameTypeAndColor?: boolean }
  | { kind: 'face', anchor: Position, face: Face, sameTypeAndColor?: boolean }
  | { kind: 'rect', min: Position, max: Position }
  | { kind: 'positions', positions: ReadonlyArray<Position> }

export type SelectionChangeEvent = readonly [
  'selection.change',
  { op?: SelectionOp, source: SelectionSource }
]

// Unified face selection event
export type FaceSelectionUnifiedEvent =
  | readonly ['selection.change.face', { op: 'set'|'add'|'remove'|'clear', face?: Face, keys?: ReadonlyArray<number> }]

// ============================================================================
// Event Creation Helper
// ============================================================================

/**
 * Helper to create event tuples without needing 'as const'
 * Usage: e(['ui.setTool', 'scribble']) instead of ['ui.setTool', 'scribble'] as const
 */
export const e = <T extends readonly unknown[]>(tuple: T): T => tuple as T
